import Box from '@mui/material/Box';
import Popper from '@mui/material/Popper';
import ArrTextField from '../../../common/arr-text-field/ArrTextField';
import {ChangeEvent, useCallback, useEffect, useRef, useState} from 'react';
import ArrLoadingSpinner from '../../../common/arr-loading-spinner/ArrLoadingSpinner';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import ArrIconButton from '../../../common/arr-icon-button/ArrIconButton';
import {ReactComponent as ChevronIcon} from '../../../../assets/images/chevron-base-icon.svg';
import {ReactComponent as Close} from '../../../../assets/images/close-base-icon.svg';
import {ReactComponent as Search} from '../../../../assets/images/icon-search.svg';
import {t} from 'i18next';
import './SearchPopper.scss';
import {useSelector} from 'react-redux';
import {RootState} from '../../../../redux/store';

interface SearchPopperProps {
  anchorEl: HTMLElement;
  loading: boolean;
  currentItem: number;
  totalItems: number;
  onClose: () => void;
  searchDocument: (str: string) => void;
  navigateResults: (index: number) => void;
  tabValue: number;
}

const SearchPopper = ({
  anchorEl,
  loading,
  currentItem,
  totalItems,
  onClose,
  searchDocument,
  navigateResults,
  tabValue,
}: SearchPopperProps) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const popperRef = useRef<HTMLDivElement | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);

  const [searchText, setSearchText] = useState('');
  const [focussed, setFocussed] = useState(false);
  const prevDisabled = currentItem < 0;
  const nextDisabled = totalItems === 0 || currentItem === totalItems - 1;
  const activeDoc = useSelector((state: RootState) => state.docView.activeDoc);

  const onFocus = useCallback(() => {
    setFocussed(true);
  }, [[]]);

  const onBlur = useCallback(() => {
    setFocussed(false);
  }, [[]]);

  useEffect(() => {
    setSearchText('');
    navigateResults(-1);
    onClose();
  }, [activeDoc, tabValue]);

  useEffect(() => {
    if (anchorEl) {
      requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
    }
  }, [anchorEl]);

  const onChange = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setSearchText(e.target.value);
    },
    [setSearchText]
  );

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        inputRef.current?.blur();
        searchDocument(searchText);
      }
    },
    [searchDocument, searchText]
  );

  const onPreviousClick = useCallback(() => {
    navigateResults(currentItem - 1);
  }, [navigateResults, currentItem]);

  const onNextClick = useCallback(() => {
    navigateResults(currentItem + 1);
  }, [navigateResults, currentItem]);

  const handleCloseButtonKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>) => {
      if (e.key === 'Tab' && !e.shiftKey) {
        e.preventDefault();
        inputRef.current?.focus();
      }
    },
    []
  );

  return (
    <Popper
      ref={popperRef}
      data-testid="prizmdoc-search-modal"
      open={Boolean(anchorEl)}
      anchorEl={anchorEl as HTMLElement}
      modifiers={[
        {
          name: 'offset',
          options: {
            offset: [0, 16],
          },
        },
      ]}
      placement="bottom-end"
      className="prizmdoc-search-box-popper"
      role="dialog"
      aria-modal="true"
    >
      <Box className="prizmdoc-search-box">
        <ArrTextField
          id="enabled-default-small"
          size="small"
          sizeVariant="small"
          dataTestId="popper-searchbox-textfield"
          className={`input-field search-field ${loading ? 'search-field-l' : ''}`}
          aria-label="Search"
          inputRef={inputRef}
          value={searchText}
          onFocus={onFocus}
          onBlur={onBlur}
          onChange={onChange}
          onKeyDown={onKeyDown}
          slotProps={{
            inputLabel: {shrink: false},
            input: {
              endAdornment: focussed ? <Search /> : null,
            },
          }}
        />
        {loading ? (
          <ArrLoadingSpinner size={12} color="secondary" />
        ) : (
          <Typography
            className="typo-text body4_size light-text"
            data-testid="search-results-selection"
          >
            {`${currentItem + 1} / ${totalItems}`}
          </Typography>
        )}
        <Divider orientation="vertical" className="panel-divider" flexItem />
        <Box className="search-controls">
          <ArrIconButton
            testId="search-results-previous-button"
            className="previous-button"
            size="small"
            icon={<ChevronIcon className="previous-icon" />}
            ariaLabel={t('docView.docPagination.previous')}
            onClick={onPreviousClick}
            disabled={prevDisabled}
            shape={prevDisabled ? 'none' : 'default'}
          />
          <ArrIconButton
            testId="search-results-next-button"
            className="next-button"
            size="small"
            icon={<ChevronIcon className="next-icon" />}
            ariaLabel={t('docView.docPagination.next')}
            onClick={onNextClick}
            disabled={nextDisabled}
            shape={nextDisabled ? 'none' : 'default'}
          />
        </Box>
        <Divider orientation="vertical" className="panel-divider" flexItem />
        <ArrIconButton
          ref={closeButtonRef}
          icon={<Close />}
          onClick={onClose}
          onKeyDown={handleCloseButtonKeyDown}
          ariaLabel="close-button"
          className="close-button"
          testId="close-btn-search"
          shape="none"
          size="small"
        />
      </Box>
    </Popper>
  );
};

export default SearchPopper;
